import type { RequestCategory } from "@nlc/shared-types";

import { Button as RACButton } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import { Button } from "@/components/button";

import type { CategoryWithIcon } from "../types";

import { DuplicatePastRequestModal } from "../../duplicate-past-request-modal";
import DownloadIcon from "../assets/download.svg?react";
import LockIcon from "../assets/lock.svg?react";
import newCapabilitiesModalImage from "../assets/new-capabilites-modal.png";
import { UpgradeModal } from "./UpgradeModal";

type HeaderProps = {
  categories: CategoryWithIcon[];
  categoryLabel: string;
  highlightedCategories: RequestCategory[];
  onCategoryChange: (categoryId: string) => void;
};

const getUpgradeModalProps = (categoryId: "graphic" | "video" | "webui") => {
  switch (categoryId) {
    case "graphic": {
      return {
        bodyText:
          "Unlock the ability to request social media ads, Amazon graphics, print designs, branding and much more!",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
    case "video": {
      return {
        bodyText:
          "Unlock the ability to request social media video ads, Amazon videos, advanced animations and long form video editing!",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
    case "webui": {
      return {
        bodyText:
          "Unlock the ability to request custom website designs, intuitive UI kits, and high-impact landing pages that drive results.",
        headerText: "Want to unlock new capabilities?",
        heroImage: newCapabilitiesModalImage,
      };
    }
  }
};

export function Header({
  categories,
  categoryLabel,
  highlightedCategories,
  onCategoryChange,
}: HeaderProps) {
  return (
    <div className="flex flex-col gap-lg lg:flex-row lg:items-center lg:justify-between border-b border-border-primary px-3xl py-xl">
      <span className="text-2xl font-semibold text-text-primary">
        {categoryLabel ? (
          <>
            Create a new{" "}
            <span className="text-fg-brand-primary font-semibold text-2xl">
              {categoryLabel}
            </span>{" "}
            request
          </>
        ) : (
          "Create a new request"
        )}
      </span>
      <div className="flex max-lg:flex-wrap items-center gap-md">
        <div className="flex max-lg:w-full divide-x divide-border-primary overflow-hidden rounded-md border border-border-primary">
          {categories.map((category) => {
            if (category.isBlocked) {
              const modalProps = getUpgradeModalProps(category.id);

              return (
                <div className="max-lg:grow" key={category.id}>
                  <UpgradeModal
                    bodyText={modalProps.bodyText}
                    headerText={modalProps.headerText}
                    heroImage={modalProps.heroImage}
                    onConfirm={() => {
                      window.open(
                        "https://nlc.com/manage-my-account/",
                        "_blank",
                      );
                    }}
                  >
                    <RACButton
                      className={twMerge(
                        "max-lg:w-full max-lg:grow flex cursor-pointer whitespace-nowrap max-lg:justify-center px-md lg:px-xl py-md max-lg:text-[clamp(10px,3.5vw,12px)] lg:text-sm font-semibold text-text-secondary outline-hidden gap-md items-center hover:bg-button-secondary-border-hover/15",
                        highlightedCategories.includes(category.id) &&
                          "bg-button-primary-bg text-text-white hover:bg-button-primary-bg-hover",
                      )}
                      type="button"
                    >
                      <LockIcon className="size-5 shrink-0" />
                      {category.name}
                    </RACButton>
                  </UpgradeModal>
                </div>
              );
            }

            return (
              <RACButton
                className={twMerge(
                  "max-lg:grow flex cursor-pointer whitespace-nowrap max-lg:justify-center px-md lg:px-xl py-md max-lg:text-[clamp(10px,3.5vw,12px)] lg:text-sm font-semibold text-text-secondary outline-hidden gap-md items-center hover:bg-button-secondary-border-hover/15",
                  highlightedCategories.includes(category.id) &&
                    "bg-button-primary-bg text-text-white hover:bg-button-primary-bg-hover",
                )}
                key={category.id}
                onPress={() => onCategoryChange(category.id)}
                type="button"
              >
                {category.icon && <category.icon className="size-5 shrink-0" />}
                {category.name}
              </RACButton>
            );
          })}
        </div>

        <DuplicatePastRequestModal>
          <Button color="gray" variant="tertiary">
            <DownloadIcon />
            Duplicate past request
          </Button>
        </DuplicatePastRequestModal>
      </div>
    </div>
  );
}
